import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Download,
  FileText,
  Send,
  X,
  Package,
  Lock,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { authenticatedDownload } from "../../lib/pdf";
import { fmtUSDate } from "../../lib/date";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

export default function LenderPackageDialog({ open, onOpenChange }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Per-document include selectors — let the borrower cherry-pick what
  // each lender sees. Defaults to "include everything" (back-compat).
  // Free-tier users have PFS/binder/comps locked off (server-enforced too).
  const [include, setInclude] = useState({
    pfs: true, rent_roll: true, lease_binder: true, comps: true,
  });
  // Plan tier — determined from preview response. Locks Pro-only docs +
  // hides Email + Share-link modes for free users.
  const isPremium = preview?.plan?.is_premium !== false;
  const toggleInclude = (key) => {
    // Free users can only toggle rent_roll. Pro-only keys are locked.
    if (!isPremium && key !== "rent_roll") return;
    setInclude((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const [mode, setMode] = useState("download");  // "download" | "email" | "share"
  // Email form
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);
  // Share link form
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareLabel, setShareLabel] = useState("");
  const [sharePassword, setSharePassword] = useState("");
  const [shareExpiresDays, setShareExpiresDays] = useState(14);
  const [shareMaxViews, setShareMaxViews] = useState("");
  const [shareNotifyOnView, setShareNotifyOnView] = useState(true);
  const [shareEmailToRecipient, setShareEmailToRecipient] = useState("");
  const [shareCreating, setShareCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  // History
  const [recentSends, setRecentSends] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [previewRes, historyRes] = await Promise.all([
          api.get("/lender-package/preview"),
          // Email history is Pro-only; swallow 402 quietly for free users.
          api.get("/lender-package/email/history").catch(() => ({ data: { items: [], recipient_suggestions: [] } })),
        ]);
        if (!cancelled) {
          setPreview(previewRes.data);
          // Free users: force-off Pro-only docs so the server-side overrride
          // is reflected in the UI checkboxes immediately.
          if (previewRes.data?.plan?.is_premium === false) {
            setInclude({ pfs: false, rent_roll: true, lease_binder: false, comps: false });
            setMode("download");  // hide email/share toggles for free users
          }
          setRecentSends(historyRes.data?.items || []);
          setSuggestions(historyRes.data?.recipient_suggestions || []);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Could not check package contents");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset email form whenever the user reopens the dialog
  useEffect(() => {
    if (open) {
      setMode("download");
      setEmailTo("");
      setEmailCc("");
      setEmailSubject("");
      setEmailMessage("");
      setShareRecipient("");
      setShareLabel("");
      setSharePassword("");
      setShareExpiresDays(14);
      setShareMaxViews("");
      setShareNotifyOnView(true);
      setShareEmailToRecipient("");
      setCreatedLink(null);
      setInclude({ pfs: true, rent_roll: true, lease_binder: true, comps: true });
    }
  }, [open]);

  const download = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams({
        pfs: include.pfs ? "true" : "false",
        rent_roll: include.rent_roll ? "true" : "false",
        lease_binder: include.lease_binder ? "true" : "false",
        comps: include.comps ? "true" : "false",
      }).toString();
      await authenticatedDownload(
        `/api/lender-package/download?${qs}`,
        `lender-package-${new Date().toISOString().slice(0, 10)}.zip`,
      );
      toast.success("Lender package downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async (e) => {
    e?.preventDefault();
    const recipients = emailTo.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      toast.error("Enter at least one recipient");
      return;
    }
    const ccList = emailCc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    setSending(true);
    try {
      const r = await api.post("/lender-package/email", {
        to: recipients,
        cc: ccList.length ? ccList : null,
        subject: emailSubject || null,
        message: emailMessage || null,
        include,
      });
      const data = r.data || {};
      const status = data.result?.status;
      if (data.ok && status !== "error") {
        toast.success(
          status === "mocked"
            ? `Mock-sent to ${recipients.join(", ")} (configure SMTP/Resend to actually send)`
            : `Lender package emailed to ${recipients.join(", ")}`
        );
        onOpenChange(false);
      } else {
        toast.error(`Email failed: ${data.result?.error || "unknown error"}. Configure SMTP/Resend in Settings.`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Email failed");
    } finally {
      setSending(false);
    }
  };

  const createShareLink = async (e) => {
    e?.preventDefault();
    if (!shareRecipient.trim()) {
      toast.error("Enter a recipient label so you can identify this link later");
      return;
    }
    const recipientEmails = shareEmailToRecipient
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setShareCreating(true);
    try {
      const r = await api.post("/lender-package/share", {
        recipient_label: shareRecipient.trim(),
        label: shareLabel.trim() || null,
        password: sharePassword.trim() || null,
        expires_in_days: shareExpiresDays ? Number(shareExpiresDays) : null,
        max_views: shareMaxViews ? Number(shareMaxViews) : null,
        notify_on_view: shareNotifyOnView,
        email_to: recipientEmails.length ? recipientEmails : null,
        include,
      });
      setCreatedLink(r.data);
      try {
        await navigator.clipboard.writeText(r.data.url);
        toast.success("Link copied to clipboard");
      } catch {
        // Browsers without clipboard permission — fall through; URL still shown.
      }
      const status = r.data.email_result?.status;
      if (recipientEmails.length) {
        if (status === "error") {
          toast.error(`Link created but email send failed: ${r.data.email_result?.error || "configure SMTP/Resend"}`);
        } else {
          toast.success(`Link emailed to ${recipientEmails.join(", ")}`);
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create share link");
    } finally {
      setShareCreating(false);
    }
  };

  const copyCreatedUrl = async () => {
    if (!createdLink?.url) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      toast.success("Link copied");
    } catch {
      toast.error("Browser blocked clipboard — copy the link manually");
    }
  };


  const items = preview ? [
    {
      key: "pfs", includeKey: "pfs", label: "Personal Financial Statement",
      ok: !!preview.pfs?.available,
      hint: preview.pfs?.available
        ? `Latest snapshot${preview.pfs.as_of_date ? ` as of ${preview.pfs.as_of_date}` : ""}`
        : "Fill out your PFS first",
    },
    {
      key: "rent_roll", includeKey: "rent_roll", label: "Portfolio rent roll (12 months)",
      ok: !!preview.rent_roll?.available,
      hint: preview.rent_roll?.available
        ? `${preview.rent_roll.lease_count} active lease${preview.rent_roll.lease_count === 1 ? "" : "s"}`
        : "Add a property + lease to enable",
    },
    {
      key: "binder", includeKey: "lease_binder", label: "Most recent year-end Lease Binder",
      ok: !!preview.lease_binder?.available,
      hint: preview.lease_binder?.available
        ? `${preview.lease_binder.year} binder · ${preview.lease_binder.lease_count || 0} leases`
        : "Generate one from Lease Vault → Binders (optional)",
    },
    {
      key: "comps", includeKey: "comps", label: "Comparable-sales summary",
      ok: !!preview.comps_summary?.available,
      hint: preview.comps_summary?.available
        ? `${preview.comps_summary.comp_count} most recent comps from your library`
        : "Run a LoopNet/Crexi search to enable (optional)",
    },
  ] : [];

  // "Required ok" now means: at least one AVAILABLE item is also CHECKED.
  // If the user toggles off everything available, we disable the action
  // buttons and show the amber hint below.
  const anyAvailable = items.some((it) => it.ok);
  const anySelectedAvailable = items.some((it) => it.ok && include[it.includeKey]);
  const requiredOk = anyAvailable && anySelectedAvailable;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pfs-lender-package-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            {isPremium ? "Generate lender package" : "Download rent roll for your lender"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isPremium
              ? "Bundles your latest PFS, a 12-month rent roll, the most recent lease binder, and a comparable-sales summary into a single ZIP. Download it or email it directly."
              : "Free plan includes a 12-month portfolio rent roll. Upgrade to Pro to add your PFS, lease binder, and comparable-sales summary — plus password-protected share links and direct lender email."}
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle — Email/Share-link are Pro-only, hidden for free */}
        {isPremium && (
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-sm w-fit">
            <button
              type="button"
              onClick={() => setMode("download")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition ${
                mode === "download" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="lp-mode-download"
            >
              Download
            </button>
            <button
              type="button"
              onClick={() => setMode("email")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition ${
                mode === "email" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="lp-mode-email"
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => setMode("share")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition ${
                mode === "share" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="lp-mode-share"
            >
              Share link
            </button>
          </div>
        )}

        {loading || !preview ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Inspecting your portfolio…
          </div>
        ) : (
          <div className="space-y-2">
            <div className="overline text-slate-700 mb-1 flex items-center justify-between">
              <span>Choose what to include</span>
              {!isPremium && (
                <Link
                  to="/dashboard/billing"
                  className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                  data-testid="lp-upgrade-pill"
                >
                  <Sparkles className="w-3 h-3" /> Pro unlocks all
                </Link>
              )}
            </div>
            {items.map((it) => {
              // Pro-only docs are locked for free users (server enforces too).
              const isProOnly = !isPremium && it.includeKey !== "rent_roll";
              const checked = it.ok && include[it.includeKey] && !isProOnly;
              const disabled = !it.ok || isProOnly;
              return (
                <label
                  key={it.key}
                  className={`flex items-start gap-3 p-3 rounded-sm border transition-colors ${
                    checked
                      ? "bg-emerald-50/60 border-emerald-300 cursor-pointer hover:bg-emerald-50"
                      : isProOnly
                        ? "bg-slate-50/60 border-slate-200 cursor-not-allowed"
                        : it.ok
                          ? "bg-white border-slate-200 cursor-pointer hover:border-slate-300"
                          : "bg-slate-50/60 border-slate-200 cursor-not-allowed opacity-70"
                  }`}
                  data-testid={`lp-item-${it.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => !disabled && toggleInclude(it.includeKey)}
                    className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
                    data-testid={`lp-include-${it.includeKey}`}
                  />
                  <div className="flex-1">
                    <div className={`text-sm font-medium flex items-center gap-2 ${
                      checked ? "text-slate-900" : it.ok && !isProOnly ? "text-slate-700" : "text-slate-500"
                    }`}>
                      {it.label}
                      {isProOnly && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-sm">
                          <Lock className="w-2.5 h-2.5" /> Pro
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {isProOnly ? "Upgrade to include this in your lender package" : it.hint}
                    </div>
                  </div>
                </label>
              );
            })}
            {!requiredOk && anyAvailable && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2" data-testid="lp-empty-selection-hint">
                Select at least one document to include in the package.
              </div>
            )}
            {!anyAvailable && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2">
                You need at least a PFS or one active lease before the package will contain any
                content. Add one of those, then try again.
              </div>
            )}
          </div>
        )}

        {/* Email form (only in email mode) */}
        {mode === "email" && (
          <form onSubmit={sendEmail} className="space-y-3 pt-2 border-t border-slate-200" data-testid="lp-email-form">
            <label className="block">
              <div className="overline text-slate-600 mb-1">Recipient(s) — comma-separated</div>
              <input
                type="text" required
                className="input-clean w-full"
                value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="loan-officer@bank.com, underwriter@bank.com"
                data-testid="lp-email-to"
                list="lp-email-suggestions"
              />
              {suggestions.length > 0 && (
                <datalist id="lp-email-suggestions">
                  {suggestions.map((s) => <option key={s} value={s} />)}
                </datalist>
              )}
            </label>
            <label className="block">
              <div className="overline text-slate-600 mb-1">CC (optional)</div>
              <input type="text" className="input-clean w-full" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} data-testid="lp-email-cc" />
            </label>
            <label className="block">
              <div className="overline text-slate-600 mb-1">Subject (optional)</div>
              <input
                type="text" className="input-clean w-full"
                value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Lender Package — auto-generated default"
                data-testid="lp-email-subject"
              />
            </label>
            <label className="block">
              <div className="overline text-slate-600 mb-1">Message (optional)</div>
              <textarea
                className="input-clean w-full min-h-[80px]"
                value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a personal note for your lender — e.g., 'As discussed on our call today...'"
                maxLength={2000}
                data-testid="lp-email-message"
              />
            </label>
            {recentSends.length > 0 && (
              <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2" data-testid="lp-recent-sends">
                <div className="font-mono uppercase tracking-widest text-slate-400 mb-1">
                  Recent sends ({recentSends.length})
                </div>
                <div className="space-y-0.5 max-h-24 overflow-y-auto">
                  {recentSends.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2">
                      <span className="truncate text-slate-600">
                        {(r.to || []).join(", ")}
                      </span>
                      <span className="text-slate-400 tabular-nums whitespace-nowrap">
                        {r.sent_at ? fmtUSDate(r.sent_at) : "—"}
                        {r.status === "error" && <span className="text-rose-500 ml-1">⚠</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}

        {/* Share-link form (only in share mode) */}
        {mode === "share" && (
          createdLink ? (
            <div className="space-y-3 pt-2 border-t border-slate-200" data-testid="lp-share-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-2">
                  Share link generated
                </div>
                <div className="flex items-center gap-2">
                  <input
                    readOnly value={createdLink.url || ""}
                    className="input-clean flex-1 text-xs font-mono"
                    data-testid="lp-share-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button" onClick={copyCreatedUrl}
                    className="btn-outline-dark text-xs px-3 py-2"
                    data-testid="lp-share-copy"
                  >Copy</button>
                </div>
                {createdLink.has_password && (
                  <div className="text-xs text-slate-600 mt-2">
                    🔒 Password-protected. Share the password with your lender on a separate channel (text/call) for security.
                  </div>
                )}
                <div className="text-xs text-slate-500 mt-2">
                  {createdLink.expires_at && (
                    <>Expires {fmtUSDate(createdLink.expires_at)} · </>
                  )}
                  {createdLink.max_views && <>Max {createdLink.max_views} view{createdLink.max_views === 1 ? "" : "s"} · </>}
                  {createdLink.notify_on_view && <>Activity Bell on each view</>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCreatedLink(null)}
                className="text-xs underline text-slate-600 hover:text-slate-900"
                data-testid="lp-share-create-another"
              >
                Create another link
              </button>
            </div>
          ) : (
            <form onSubmit={createShareLink} className="space-y-3 pt-2 border-t border-slate-200" data-testid="lp-share-form">
              <label className="block">
                <div className="overline text-slate-600 mb-1">Recipient — required</div>
                <input
                  type="text" required
                  className="input-clean w-full"
                  value={shareRecipient} onChange={(e) => setShareRecipient(e.target.value)}
                  placeholder="Wells Fargo, Bank of America, etc."
                  data-testid="lp-share-recipient"
                />
                <div className="text-[11px] text-slate-400 mt-1">Shown on the public page so the lender knows the link is for them.</div>
              </label>
              <label className="block">
                <div className="overline text-slate-600 mb-1">Internal label (optional)</div>
                <input
                  type="text" className="input-clean w-full"
                  value={shareLabel} onChange={(e) => setShareLabel(e.target.value)}
                  placeholder="e.g., 2026 acquisition refinance"
                  data-testid="lp-share-label"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Password (optional)</div>
                  <input
                    type="text" className="input-clean w-full"
                    value={sharePassword} onChange={(e) => setSharePassword(e.target.value)}
                    placeholder="Recommended for sensitive shares"
                    data-testid="lp-share-password"
                  />
                </label>
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Expires in (days)</div>
                  <input
                    type="number" min="1" max="365"
                    className="input-clean w-full"
                    value={shareExpiresDays}
                    onChange={(e) => setShareExpiresDays(e.target.value)}
                    data-testid="lp-share-expires-days"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Max views (optional)</div>
                  <input
                    type="number" min="1" max="1000"
                    className="input-clean w-full"
                    value={shareMaxViews}
                    onChange={(e) => setShareMaxViews(e.target.value)}
                    placeholder="Unlimited"
                    data-testid="lp-share-max-views"
                  />
                </label>
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Activity Bell on view</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input
                      type="checkbox" checked={shareNotifyOnView}
                      onChange={(e) => setShareNotifyOnView(e.target.checked)}
                      data-testid="lp-share-notify-on-view"
                    />
                    <span className="text-sm text-slate-600">Notify me when each lender opens the link</span>
                  </div>
                </label>
              </div>
              <label className="block">
                <div className="overline text-slate-600 mb-1">Also email link to (optional)</div>
                <input
                  type="text" className="input-clean w-full"
                  value={shareEmailToRecipient}
                  onChange={(e) => setShareEmailToRecipient(e.target.value)}
                  placeholder="loan-officer@bank.com"
                  data-testid="lp-share-email-to"
                />
                <div className="text-[11px] text-slate-400 mt-1">
                  We'll email this lender the public URL right away. Many bank inboxes strip ZIP attachments — a clickable link is friendlier.
                </div>
              </label>
            </form>
          )
        )}

        <DialogFooter className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-outline-dark inline-flex items-center gap-1 px-3"
            data-testid="lp-cancel"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          {mode === "download" && (
            <button
              type="button"
              onClick={download}
              disabled={loading || !preview || !requiredOk || downloading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="lp-download"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download ZIP
            </button>
          )}
          {mode === "email" && (
            <button
              type="button"
              onClick={sendEmail}
              disabled={loading || !preview || !requiredOk || sending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="lp-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to lender
            </button>
          )}
          {mode === "share" && !createdLink && (
            <button
              type="button"
              onClick={createShareLink}
              disabled={loading || !preview || !requiredOk || shareCreating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="lp-share-create"
            >
              {shareCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              Generate share link
            </button>
          )}
          {mode === "share" && createdLink && (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm"
              data-testid="lp-share-done"
            >
              Done
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
